import { FC } from 'react';

import { DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';

import { GridColumn, GridLayout, GridRow, useBreakpoint } from '@hh.ru/magritte-ui';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import { useCampaignsDashboardRedesignAllowed } from 'hooks/useCampaignsDashboardRedesignAllowed';

import { InternalRedirect } from 'app/routes/InternalRedirect/InternalRedirect';

import { RemainderBudgetsModal } from 'components/CampaignAdminActions/RemainderBudgetsModal/RemainderBudgetsModal';
import { SourceAccountsModal } from 'components/CampaignAdminActions/SourceAccountsModal/SourceAccountsModal';
import { CampaignsDashboard } from 'components/CampaignsDashboard/CampaignsDashboard';
import { ArchiveCampaignAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/ArchiveCampaignAlert';
import { CampaignBudgetExceededAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/CampaignBudgetExceededAlert';
import { CampaignInactiveDueToEndDateAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/CampaignInactiveDueToEndDateAlert';
import { CampaignInsufficientBalanceAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/CampaignInsufficientBalanceAlert';
import { ModerationFailedAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/CampaignModerationFailedAlert';
import { StopCampaignAlert } from 'components/CampaignsDashboard/components/CampaignsTable/components/alerts/StopCampaignAlert';
import BalanceReplenishmentModal from 'components/common/BalanceReplenishmentModal/BalanceReplenishmentModal';
import { BudgetIncreaseModal } from 'components/common/BudgetIncreaseModal/BudgetIncreaseModal';
import { ChangeHistoryModal } from 'components/common/ChangeHistoryModal/ChangeHistoryModal';
import NeedToReplenishAlert from 'components/common/NeedToReplenishAlert/NeedToReplenishAlert';
import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { DownloadStatisticButton } from 'components/Dashboard/DownloadStatisticButton/DownloadStatisticButton';
import { WebinarBanner } from 'components/Dashboard/WebinarBanner/WebinarBanner';
import { WEBINAR_BANNER_CONFIG } from 'components/Dashboard/WebinarBanner/webinarBannerConfig';
import { YandexVideoBanner } from 'components/Dashboard/YandexVideoBanner/YandexVideoBanner';
import { Header } from 'components/Header/Header';

import { selectIsWebinarBannerVisible } from 'store/autoReducers';
import { useSelector } from 'store/utils';

const TrlKeys = {
    title: 'CampaignsDashboardPage.title',
};

export const CampaignsDashboardPage: FC = () => {
    const isWebinarBannerVisible = useSelector(selectIsWebinarBannerVisible);
    const [
        isBalanceReplenishmentModalVisible,
        setIsBalanceReplenishmentModalVisibleOn,
        setIsBalanceReplenishmentModalVisibleOff,
    ] = useOnOffState(false);

    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const isCampaignsDashboardRedesign = useCampaignsDashboardRedesignAllowed();

    if (!isCampaignsDashboardRedesign) {
        return <InternalRedirect to={DASHBOARD_PAGE_ROUTE} />;
    }

    return (
        <>
            <Header />
            <GridLayout>
                <GridRow>
                    <GridColumn xs={4} s={8} m={12}>
                        <WebinarBanner {...WEBINAR_BANNER_CONFIG} />
                        {!isWebinarBannerVisible && <YandexVideoBanner />}
                    </GridColumn>
                </GridRow>
            </GridLayout>
            <PageContentHeader
                title={trls(TrlKeys.title)}
                vSpacingBottom={isMobile ? 16 : 0}
                rightContent={<DownloadStatisticButton isDashboardRedesign={true} />}
                mobileRightContentInline
                mobileVSpacingTop={16}
            />
            <CampaignsDashboard />
            <ChangeHistoryModal />
            <RemainderBudgetsModal />
            <SourceAccountsModal />
            <BudgetIncreaseModal />
            <ArchiveCampaignAlert />
            <StopCampaignAlert />
            <ModerationFailedAlert />
            <CampaignInactiveDueToEndDateAlert />
            <CampaignBudgetExceededAlert />
            <CampaignInsufficientBalanceAlert
                setIsBalanceReplenishmentModalVisibleOn={setIsBalanceReplenishmentModalVisibleOn}
            />
            <NeedToReplenishAlert openBalanceReplenishmentModal={setIsBalanceReplenishmentModalVisibleOn} />
            <BalanceReplenishmentModal
                withDescription={false}
                isVisible={isBalanceReplenishmentModalVisible}
                closeModal={setIsBalanceReplenishmentModalVisibleOff}
            />
        </>
    );
};
